import React, { useState, useEffect } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Stack,
	Typography,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Checkbox,
	Chip,
	Box
} from "@mui/material";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import {
	listCatalogProducts,
	sendSingleProduct,
	sendMultiProduct
} from "../../services/wabaFeatures";

const MAX = 30; // limite da Meta para multi-produto

const conjuntoOf = p => p.product_type || "Sem conjunto";

// Envia produto(s) do catálogo. Selecione um CONJUNTO -> todos os itens dele já
// vêm marcados (pode desmarcar). 1 selecionado = produto único; 2+ = lista.
const WabaProductModal = ({ open, onClose, whatsappId, to, ticketId }) => {
	const [catalogId, setCatalogId] = useState(null);
	const [products, setProducts] = useState([]);
	const [conjunto, setConjunto] = useState("Todos");
	const [selected, setSelected] = useState([]);
	const [bodyText, setBodyText] = useState("Confira nossos produtos:");
	const [loading, setLoading] = useState(false);
	const [sending, setSending] = useState(false);

	useEffect(() => {
		if (!open || !whatsappId) return;
		const load = async () => {
			setLoading(true);
			try {
				const { data } = await listCatalogProducts(whatsappId);
				const d = data?.data || {};
				const prods = d.products || [];
				setCatalogId(d.catalogId || null);
				setProducts(prods);
				setConjunto("Todos");
				setSelected(prods.slice(0, MAX).map(p => p.retailer_id)); // tudo marcado
			} catch (err) {
				toastError(err);
			} finally {
				setLoading(false);
			}
		};
		load();
	}, [open, whatsappId]);

	const conjuntos = ["Todos", ...Array.from(new Set(products.map(conjuntoOf)))];
	const filtered =
		conjunto === "Todos" ? products : products.filter(p => conjuntoOf(p) === conjunto);

	// Trocar de conjunto -> marca TODOS os itens do conjunto (respeitando o limite).
	const pickConjunto = c => {
		setConjunto(c);
		const list = c === "Todos" ? products : products.filter(p => conjuntoOf(p) === c);
		const ids = list.map(p => p.retailer_id).slice(0, MAX);
		if (list.length > MAX) {
			toast.warn(`O conjunto tem ${list.length} itens; a Meta envia no máximo ${MAX}. Marquei os ${MAX} primeiros.`);
		}
		setSelected(ids);
	};

	const toggle = rid => {
		setSelected(s => {
			if (s.includes(rid)) return s.filter(x => x !== rid);
			if (s.length >= MAX) {
				toast.warn(`Máximo de ${MAX} produtos por envio.`);
				return s;
			}
			return [...s, rid];
		});
	};

	const markAll = () =>
		setSelected(filtered.map(p => p.retailer_id).slice(0, MAX));
	const unmarkAll = () => setSelected([]);

	const handleSend = async () => {
		if (!catalogId || selected.length === 0) {
			toast.error("Selecione ao menos um produto.");
			return;
		}
		setSending(true);
		try {
			if (selected.length === 1) {
				const prod = products.find(p => p.retailer_id === selected[0]);
				await sendSingleProduct(whatsappId, {
					to,
					ticketId,
					catalogId,
					productRetailerId: selected[0],
					bodyText,
					label: prod?.name
				});
			} else {
				await sendMultiProduct(whatsappId, {
					to,
					ticketId,
					catalogId,
					headerText: conjunto === "Todos" ? "Nossos produtos" : conjunto,
					bodyText,
					sections: [{ title: conjunto === "Todos" ? "Produtos" : conjunto, productRetailerIds: selected }]
				});
			}
			toast.success("Produto(s) enviado(s)!");
			onClose();
		} catch (err) {
			toastError(err);
		} finally {
			setSending(false);
		}
	};

	return (
		<Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Enviar produto do catálogo</DialogTitle>
			<DialogContent>
				{loading ? (
					<Typography sx={{ py: 2 }}>Carregando produtos…</Typography>
				) : !catalogId || products.length === 0 ? (
					<Typography sx={{ py: 2 }}>
						Nenhum catálogo/produto disponível nesta conta.
					</Typography>
				) : (
					<Stack spacing={2} sx={{ mt: 1 }}>
						<TextField
							label="Mensagem"
							value={bodyText}
							onChange={e => setBodyText(e.target.value)}
							fullWidth
							multiline
							minRows={2}
						/>
						<Box>
							<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
								Conjunto (marca todos do grupo):
							</Typography>
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
								{conjuntos.map(c => (
									<Chip
										key={c}
										label={c}
										size="small"
										color={conjunto === c ? "primary" : "default"}
										variant={conjunto === c ? "filled" : "outlined"}
										onClick={() => pickConjunto(c)}
									/>
								))}
							</Box>
						</Box>
						<Box sx={{ display: "flex", gap: 1 }}>
							<Button size="small" onClick={markAll}>Marcar todos</Button>
							<Button size="small" onClick={unmarkAll}>Desmarcar todos</Button>
						</Box>
						<List dense sx={{ maxHeight: 240, overflow: "auto" }}>
							{filtered.map(p => (
								<ListItem key={p.retailer_id} disablePadding>
									<ListItemButton onClick={() => toggle(p.retailer_id)}>
										<ListItemIcon>
											<Checkbox edge="start" checked={selected.includes(p.retailer_id)} tabIndex={-1} />
										</ListItemIcon>
										<ListItemText primary={p.name} secondary={p.price} />
									</ListItemButton>
								</ListItem>
							))}
						</List>
					</Stack>
				)}
			</DialogContent>
			<DialogActions sx={{ p: 2, gap: 1 }}>
				<Button onClick={onClose} disabled={sending}>Cancelar</Button>
				<Button
					onClick={handleSend}
					variant="contained"
					disabled={sending || loading || !catalogId || selected.length === 0}
				>
					{sending ? "Enviando..." : `Enviar${selected.length ? ` (${selected.length})` : ""}`}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default WabaProductModal;
